import { useMemo, useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { AlertCircle, Lightbulb, Paperclip, Send } from 'lucide-react';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { db, storage } from '../../services/firebase';
import { useAuthStore } from '../../stores/auth-store';
import { useProjectsStore } from '../../stores/projects-store';

type FeedbackType = 'bug' | 'suggestion';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const user = useAuthStore((s) => s.user);
  const currentProject = useProjectsStore((s) => s.currentProject);
  const [type, setType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const canSubmit = useMemo(() => message.trim().length > 0 && !submitting, [message, submitting]);

  const reset = () => {
    setType('bug');
    setMessage('');
    setImageFile(null);
    setSubmitting(false);
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!user?.uid) {
      setError('Нужно войти в аккаунт.');
      return;
    }
    if (!message.trim()) {
      setError('Опишите, что произошло.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      let imageUrl: string | null = null;
      let imagePath: string | null = null;

      if (imageFile) {
        const safeName = sanitizeFileName(imageFile.name);
        imagePath = `feedback/${user.uid}/${Date.now()}-${safeName}`;
        const imageRef = ref(storage, imagePath);
        await uploadBytes(imageRef, imageFile, {
          contentType: imageFile.type || 'application/octet-stream',
        });
        imageUrl = await getDownloadURL(imageRef);
      }

      await addDoc(collection(db, 'feedback_reports'), {
        type,
        message: message.trim(),
        imageUrl,
        imagePath,
        userId: user.uid,
        userEmail: user.email ?? null,
        userDisplayName: user.displayName ?? null,
        projectId: currentProject?.id ?? null,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        status: 'new',
        createdAt: serverTimestamp(),
      });

      setSuccess('Спасибо! Информация отправлена.');
      setTimeout(() => handleClose(), 500);
    } catch (e) {
      const text = e instanceof Error ? e.message : String(e);
      setError(text);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title="Отправить инфо">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setType('bug')}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded border text-sm transition-colors ${
              type === 'bug'
                ? 'bg-red-500/10 border-red-400/50 text-red-300'
                : 'bg-surface border-surface-lighter text-text-muted hover:text-text'
            }`}
          >
            <AlertCircle size={14} />
            Ошибка
          </button>
          <button
            onClick={() => setType('suggestion')}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded border text-sm transition-colors ${
              type === 'suggestion'
                ? 'bg-emerald-500/10 border-emerald-400/50 text-emerald-300'
                : 'bg-surface border-surface-lighter text-text-muted hover:text-text'
            }`}
          >
            <Lightbulb size={14} />
            Предложение
          </button>
        </div>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder={type === 'bug' ? 'Опишите ошибку и шаги воспроизведения...' : 'Опишите вашу идею...'}
          className="w-full px-3 py-2 bg-surface border border-surface-lighter rounded text-sm text-text placeholder:text-text-muted/70 resize-none focus:outline-none focus:border-accent"
        />

        <label className="inline-flex items-center gap-2 text-sm text-text-muted cursor-pointer">
          <Paperclip size={14} />
          <span>Прикрепить фото</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {imageFile && (
          <p className="text-xs text-text-muted">Файл: {imageFile.name}</p>
        )}

        {error && <div className="rounded bg-red-900/30 text-red-300 text-xs px-3 py-2">{error}</div>}
        {success && <div className="rounded bg-emerald-900/30 text-emerald-300 text-xs px-3 py-2">{success}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center justify-center"
          >
            <Send size={14} />
            <span className="ml-1.5">{submitting ? 'Отправка...' : 'Отправить'}</span>
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
